import { createAsyncThunk } from "@reduxjs/toolkit";
import { ISignature } from "./signature.types";
import { IDataTableV2DateState } from "../../components/TableV2/preDefinedPlugins/DataTableV2Date/DataTableV2Date.types";
import { IDatatableV2AdvancedSearchFilter } from "../../components/TableV2/preDefinedPlugins/SearchFilter/SearchFilter.types";
import { APIError } from "../../constants/types";
import { api } from "../../api/api";
import { ERROR_MESSAGES, LoadState } from "../../constants/enums";
import { IStoreState } from "../initialStoreState";

export const fetchSignaturesList = createAsyncThunk<
  {
    data: ISignature[];
  },
  {
    pageNumber: number;
    rowsInPerPage: number;
    limit: number;
    status: string;
    date: IDataTableV2DateState["dates"];
    searchValue: IDatatableV2AdvancedSearchFilter;
  },
  { rejectValue: APIError }
>(
  "signature/fetchSignaturesList",
  async (
    { pageNumber, rowsInPerPage, limit, status, date, searchValue },
    { rejectWithValue },
  ) => {
    try {
      let finalUrl = `/outlook/signatures?pageNo=${pageNumber}&pageLimit=${limit}&itemPerPage=${rowsInPerPage}&from_date=${date.fromDate}&to_date=${date.toDate}`;
      if (searchValue.length > 0) {
        finalUrl = `/outlook/signatures?pageNo=${pageNumber}&pageLimit=${limit}&itemPerPage=${rowsInPerPage}&from_date=${date.fromDate}&to_date=${date.toDate}&advanceFilter=${JSON.stringify(searchValue)}`;
      }
      if (status !== "-1") {
        finalUrl += "&status=" + status;
      }
      const res = await api.get(finalUrl);
      const data: ISignature[] = res.data.data;
      return { data };
    } catch (err: any) {
      return rejectWithValue({
        message: err.response?.data?.message || ERROR_MESSAGES.SERVER_ERROR,
        status: err.response?.status,
      });
    }
  },
);

export const createSignature = createAsyncThunk<
  {
    data: string;
  },
  {
    payload: {
      signature_name: string;
      html_content: string;
      is_default: number;
    };
  },
  { rejectValue: APIError }
>(
  "signature/createSignature",
  async ({ payload }, { rejectWithValue }) => {
    try {
      const res = await api.post(`/outlook/signature`, payload);
      return { data: res.data.message || "Signature created successfully" };
    } catch (err: any) {
      return rejectWithValue({
        message: err.response?.data?.message || ERROR_MESSAGES.SERVER_ERROR,
        status: err.response?.status,
      });
    }
  },
);

export const updateSignature = createAsyncThunk<
  {
    data: string;
  },
  {
    signature_code: string;
    payload: {
      signature_name: string;
      html_content: string;
      is_default: number;
    };
  },
  { rejectValue: APIError }
>(
  "signature/updateSignature",
  async ({ signature_code, payload }, { rejectWithValue }) => {
    try {
      const res = await api.put(`/outlook/signature/${signature_code}`, payload);
      return { data: res.data.message || "Signature updated successfully" };
    } catch (err: any) {
      return rejectWithValue({
        message: err.response?.data?.message || ERROR_MESSAGES.SERVER_ERROR,
        status: err.response?.status,
      });
    }
  },
);

export const deleteSignature = createAsyncThunk<
  {
    data: string;
  },
  {
    signature_code: string;
  },
  { rejectValue: APIError }
>(
  "signature/deleteSignature",
  async ({ signature_code }, { rejectWithValue }) => {
    try {
      const res = await api.delete(`/outlook/signatures/${signature_code}`);
      return { data: res.data.message || "Signature deleted successfully" };
    } catch (err: any) {
      return rejectWithValue({
        message: err.response?.data?.message || ERROR_MESSAGES.SERVER_ERROR,
        status: err.response?.status,
      });
    }
  },
);

export const fetchAllSignatures = createAsyncThunk<
  {
    data: ISignature[];
  },
  void,
  { rejectValue: APIError }
>(
  "signature/fetchAllSignatures",
  async (_, { rejectWithValue }) => {
    try {
      const res = await api.get(`/outlook/signatures`);
      const data: ISignature[] = res.data.data;
      return { data };
    } catch (err: any) {
      return rejectWithValue({
        message: err.response?.data?.message || ERROR_MESSAGES.SERVER_ERROR,
        status: err.response?.status,
      });
    }
  },
  {
    condition: (_, { getState }) => {
      const { signature } = getState() as IStoreState;
      if (
        signature.allSignaturesLoading === LoadState.Loaded &&
        signature.allSignatures.length > 0
      ) {
        return false;
      }
      return true;
    },
  },
);
